import {useEffect, useState, useCallback} from 'react';
import Cards from './components/Cards.jsx';
import StackedByKey from './components/charts/StackedByKey.jsx';
import Composition from './components/charts/Composition.jsx';
import Profitability from './components/charts/Profitability.jsx';
import BalanceLines from './components/charts/BalanceLines.jsx';
import MonthTable from './components/MonthTable.jsx';
import Browse, {VIEWS} from './components/Browse.jsx';
import PeriodPicker from './components/PeriodPicker.jsx';
import Classify from './components/Classify.jsx';
import Simulate from './components/Simulate.jsx';
import Sync from './components/Sync.jsx';
import Grid from './components/Grid.jsx';
import Login from './components/Login.jsx';
import Invoices from './components/Invoices.jsx';
import Runway from './components/Runway.jsx';
import Shadow from './components/Shadow.jsx';
import Periodic from './components/Periodic.jsx';
import Boards from './components/Boards.jsx';
import {api, refresh, logout} from './lib/auth.js';
import {kr} from './lib/format.js';
import {toPeriods, GRAINS} from './lib/periods.js';

/** Bank accounts, only those with movement — a line flat at zero for 66 months is noise. */
const CASH_SERIES = [
	{key: 'cash', label: 'Totalt', emphasis: true, dash: null},
	{key: 'account1930', label: '1930 Företagskonto', dash: '6 4'},
	{key: 'account1945', label: '1945 e-Kapitalkonto', dash: '2 3'},
	{key: 'account1942', label: '1942 Placeringskonto', dash: '8 3 2 3'},
	{key: 'taxAccount', label: '1630 Skattekonto', dash: '1 4'},
];

/**
 * Month / quarter / half-year / year. ONE control drives the charts and the table
 * together: two grain switches could disagree, and a chart showing quarters above a
 * table showing months is the kind of mismatch a reader carries away as a wrong number.
 */
function GrainPicker({value, onChange}) {
	return (
		<span className="grain">
			<span className="grain__label">Visa per</span>
			{Object.entries(GRAINS).map(([key, g]) => (
				<button key={key} className="seg" aria-pressed={value === key}
					onClick={() => onChange(key)}>{g.label}</button>
			))}
		</span>
	);
}

/**
 * The nav, grouped by what a person is DOING rather than by which collection backs it.
 *
 * Flat, the tab bar had grown to fourteen items in no order — "Artiklar" sat beside
 * "Klassificering" because both happened to be browse views. Grouping is by domain:
 * Register is master data, Försäljning and Inköp are the two document flows, Bokföring is
 * the ledger, Sandbox is everything that models rather than records.
 *
 * Top-level entries stay flat when they are the things looked at most often — a dashboard
 * behind a menu is a dashboard nobody opens.
 */
const NAV = [
	{key: 'dashboard', label: 'Dashboard'},
	{key: 'periodic', label: 'Periodisk vy'},
	{key: 'runway', label: 'Runway'},
	{key: 'boards', label: 'Tavlor'},
	{label: 'Register', items: [
		['articles', VIEWS.articles.label],
		['customers', VIEWS.customers.label],
		['suppliers', VIEWS.suppliers.label],
		['grid', 'Tabeller'],
	]},
	{label: 'Försäljning', items: [
		['customer-invoices', VIEWS['customer-invoices'].label],
		['invoices', 'Skapa faktura'],
	]},
	{label: 'Inköp', items: [
		['supplier-invoices', VIEWS['supplier-invoices'].label],
	]},
	{label: 'Bokföring', items: [
		['vouchers', VIEWS.vouchers.label],
	]},
	{label: 'Sandbox', items: [
		['classify', 'Klassificering'],
		['simulate', 'Simulering'],
		['shadow', 'Skuggbokföring'],
	]},
	{key: 'sync', label: 'Synk'},
];

/** Flattened, for hash routing and the title lookup — the menus are presentation only. */
const TABS = NAV.flatMap(n => (n.items ? n.items : [[n.key, n.label]]));

/**
 * A nav group. At MODULE scope for the same reason `Chrome` is: a component declared
 * inside a render is a new type every render and remounts its whole subtree.
 *
 * Opens on click rather than hover — a hover menu over a dense table is a trap on the way
 * to the data — and closes on selection, on Escape, and on any click outside it.
 */
function Menu({n, tab, setTab}) {
	const [open, setOpen] = useState(false);
	const active = n.items.some(([k]) => k === tab);
	useEffect(() => {
		if (!open) return undefined;
		const close = () => setOpen(false);
		const onKey = e => e.key === 'Escape' && setOpen(false);
		window.addEventListener('click', close);
		window.addEventListener('keydown', onKey);
		return () => {
			window.removeEventListener('click', close);
			window.removeEventListener('keydown', onKey);
		};
	}, [open]);
	const current = n.items.find(([k]) => k === tab);
	return (
		<span className="menu" onClick={e => e.stopPropagation()}>
			<button onClick={() => setOpen(o => !o)} aria-current={active ? 'page' : undefined}
				aria-expanded={open} aria-haspopup="menu">
				{n.label}
				{current && <span className="menu__cur"> · {current[1]}</span>}
				<span className="caret caret--down" />
			</button>
			{open && (
				<span className="menu__pop" role="menu">
					{n.items.map(([key, label]) => (
						<button key={key} role="menuitem" aria-current={tab === key ? 'page' : undefined}
							onClick={() => { setTab(key); setOpen(false); }}>{label}</button>
					))}
				</span>
			)}
		</span>
	);
}

/**
 * Header and tab bar. Defined at MODULE scope, not inside App: a component declared
 * inside a render is a NEW type on every render, so React unmounts and remounts the
 * whole subtree each time state changes. That silently reset PeriodPicker's range
 * anchor on the first click, making range selection impossible — and remounted every
 * chart on every keystroke.
 */
function Chrome({tab, setTab, session, onSignOut, shadow, onShadow, shadowCount, children}) {
	return (
		<div className="shell">
			<div className="head">
				<h1 className="h1">Jabr</h1><span className="ar">جبر</span>
				<span className="stamp">· {TABS.find(([k]) => k === tab)?.[1]}</span>
				<span className="spacer" />
				{session && (
					<span className="who">
						{/* The toggle lives in the CHROME, not on one tab. It changes every figure in
						    the app, and a control with that reach being reachable only from the page
						    that owns the data is how someone reads a booked-only report, sees a cash
						    balance they know is wrong, and has no way to find out why. */}
						<label className={`shadowtog${shadow ? ' shadowtog--on' : ''}`}>
							<input type="checkbox" checked={shadow} onChange={e => onShadow(e.target.checked)} />
							<span>Skuggbokföring{shadowCount ? ` (${shadowCount})` : ''}</span>
						</label>
						<span className="stamp">{session.email} · {session.organization?.name ?? session.organization?.id}</span>
						<button className="btn btn--sm" onClick={onSignOut}>Logga ut</button>
					</span>
				)}
			</div>
			<nav className="nav">
				{NAV.map(n => (n.items
					? <Menu key={n.label} n={n} tab={tab} setTab={setTab} />
					: (
						<button key={n.key} onClick={() => setTab(n.key)}
							aria-current={tab === n.key ? 'page' : undefined}>{n.label}</button>
					)))}
			</nav>
			{children}
		</div>
	);
}

export default function App() {
	// null = still deciding. The app cannot render either the dashboard or the login form
	// until the refresh cookie has been given its chance, or a reload would bounce a
	// signed-in user to the login screen for a moment on every refresh.
	const [session, setSession] = useState(null);
	const [tab, setTab] = useState(() => {
		const h = window.location.hash.slice(1);
		return TABS.some(([k]) => k === h) ? h : 'dashboard';
	});
	const [data, setData] = useState(null);
	const [error, setError] = useState(null);
	const [period, setPeriod] = useState(null);
	const [busy, setBusy] = useState(false);
	const [grain, setGrain] = useState('month');
	/**
	 * Whether provisional entries are folded into every figure. OFF by default and kept in
	 * localStorage so it survives a reload — but it is deliberately NOT in the URL: a link
	 * someone pastes into a board pack must not silently carry an estimate along with it.
	 */
	const [shadow, setShadow] = useState(() => {
		try { return localStorage.getItem('jabr.shadow') === '1'; } catch { return false; }
	});
	// How many provisional entries EXIST, regardless of whether they are applied. Without
	// this the header cannot distinguish "none" from "some, switched off".
	const [shadowCount, setShadowCount] = useState(0);
	const toggleShadow = useCallback(v => {
		setShadow(v);
		try { localStorage.setItem('jabr.shadow', v ? '1' : '0'); } catch { /* private mode */ }
	}, []);

	const load = useCallback(async () => {
		setBusy(true);
		try {
			const res = await api(`/api/overview${shadow ? '?shadow=1' : ''}`);
			if (!res.ok) throw new Error(`API svarade ${res.status}`);
			setData(await res.json());
			setError(null);
		} catch (e) {
			setError(e.message);
		} finally {
			setBusy(false);
		}
	}, [shadow]);

	// Boot: the httpOnly refresh cookie is the only evidence of a session, and asking for
	// an access token is the only way to test it.
	useEffect(() => {
		let live = true;
		refresh()
			.then(() => api('/api/auth/me').then(r => (r.ok ? r.json() : null)))
			.then(me => live && setSession(me ?? false))
			.catch(() => live && setSession(false));
		return () => { live = false; };
	}, []);

	useEffect(() => { if (session) load(); }, [session, load]);
	useEffect(() => {
		if (!session) return;
		api('/api/shadow').then(r => (r.ok ? r.json() : null))
			.then(j => setShadowCount(j?.summary?.count ?? 0)).catch(() => {});
	}, [session, shadow]);
	useEffect(() => {
		window.location.hash = tab === 'dashboard' ? '' : tab;
	}, [tab]);
	// Back/forward and a pasted #hash must both switch the view — without this the
	// URL and the visible tab drift apart, which is worse than having no routing.
	useEffect(() => {
		const onHash = () => {
			const h = window.location.hash.slice(1);
			setTab(TABS.some(([k]) => k === h) ? h : 'dashboard');
		};
		window.addEventListener('hashchange', onHash);
		return () => window.removeEventListener('hashchange', onHash);
	}, []);

	if (session === null) return <div className="shell"><div className="state">…</div></div>;
	if (!session) {
		return <Login onSignedIn={() => api('/api/auth/me')
			.then(r => r.json()).then(setSession).catch(() => setSession(false))} />;
	}

	const signOut = () => logout().then(() => { setSession(false); setData(null); });

	if (tab === 'periodic') return <Chrome tab={tab} setTab={setTab} session={session} onSignOut={signOut} shadow={shadow} onShadow={toggleShadow} shadowCount={shadowCount}><Periodic shadow={shadow} /></Chrome>;
	if (tab === 'runway') return <Chrome tab={tab} setTab={setTab} session={session} onSignOut={signOut} shadow={shadow} onShadow={toggleShadow} shadowCount={shadowCount}><Runway shadow={shadow} /></Chrome>;
	if (tab === 'boards') return <Chrome tab={tab} setTab={setTab} session={session} onSignOut={signOut} shadow={shadow} onShadow={toggleShadow} shadowCount={shadowCount}><Boards /></Chrome>;
	if (tab === 'shadow') return <Chrome tab={tab} setTab={setTab} session={session} onSignOut={signOut} shadow={shadow} onShadow={toggleShadow} shadowCount={shadowCount}><Shadow active={shadow} onToggle={toggleShadow} /></Chrome>;
	if (tab === 'classify') return <Chrome tab={tab} setTab={setTab} session={session} onSignOut={signOut} shadow={shadow} onShadow={toggleShadow} shadowCount={shadowCount}><Classify /></Chrome>;
	if (tab === 'simulate') return <Chrome tab={tab} setTab={setTab} session={session} onSignOut={signOut} shadow={shadow} onShadow={toggleShadow} shadowCount={shadowCount}><Simulate /></Chrome>;
	if (tab === 'sync') return <Chrome tab={tab} setTab={setTab} session={session} onSignOut={signOut} shadow={shadow} onShadow={toggleShadow} shadowCount={shadowCount}><Sync /></Chrome>;
	if (tab === 'grid') return <Chrome tab={tab} setTab={setTab} session={session} onSignOut={signOut} shadow={shadow} onShadow={toggleShadow} shadowCount={shadowCount}><Grid /></Chrome>;
	if (tab === 'invoices') return <Chrome tab={tab} setTab={setTab} session={session} onSignOut={signOut} shadow={shadow} onShadow={toggleShadow} shadowCount={shadowCount}><Invoices /></Chrome>;
	if (tab !== 'dashboard') return <Chrome tab={tab} setTab={setTab} session={session} onSignOut={signOut} shadow={shadow} onShadow={toggleShadow} shadowCount={shadowCount}><Browse collection={tab} /></Chrome>;

	if (error) return <div className="shell"><div className="state">
		Kunde inte hämta data: {error}. Kör API:t på port 9998 och ladda om.
	</div></div>;
	if (!data) return <div className="shell"><div className="state">Hämtar…</div></div>;

	// null = the whole imported history. A range is inclusive on both ends. toPeriods does
	// the windowing itself: a period's opening cohort sits one month OUTSIDE the window,
	// so it needs the full series to compute retention for the first period shown.
	// Every row — month or rolled-up period — comes back in the same shape, so no chart
	// below needs to know which grain it is drawing.
	const months = toPeriods(data.months, grain, period);
	const g = GRAINS[grain];
	const {kpi, counts} = data;
	// A period can legitimately contain no months — Q4 2026 is entirely past the last
	// booked month. That is an empty state, not an error, and the picker must stay
	// visible so the user can pick their way back out of it.
	const empty = months.length === 0;
	// The partly-booked month is whichever one the API flags, never a month named in the
	// copy: this panel said "Augusti" for weeks after August was booked. Only the reasons
	// that actually hold are stated.
	const partial = data.months.find(m => m.ledger_partial);
	const partialName = partial ? `${new Date(`${partial.month}-01`).toLocaleString('sv-SE', {month: 'long'})} ${partial.month.slice(0, 4)}` : '';
	const partialWhy = partial && [
		kpi.unbooked > 0.5 && `${kr(kpi.unbooked)} kr är fakturerat men ännu inte bokfört`,
		!(partial.personnel < -1000) && 'lönekörningen är inte bokförd',
	].filter(Boolean).join(', och ').replace(/^./, c => c.toUpperCase())
		|| `Månaden pågår, och huvudboken stänger veckor efter banken`;

	return (
		<Chrome tab={tab} setTab={setTab} session={session} onSignOut={signOut} shadow={shadow} onShadow={toggleShadow} shadowCount={shadowCount}>
			<p className="sub">Abonnemangsintäkt, retention och runway för Pifa AB — ur Fortnox huvudbok och fakturaregister.</p>

			<PeriodPicker first={data.first_month} last={data.cutoff}
				value={period} onChange={setPeriod} />

			<div className="toolbar">
				<GrainPicker value={grain} onChange={setGrain} />
				<span className="stamp">
					{months.length} {months.length === 1 ? g.sv : g.plural}
					{period ? ` · ${period.from} – ${period.to}` : ' · hela historiken'}
				</span>
				<span className="spacer" />
				<span className="stamp">
					{counts.vouchers.toLocaleString('sv-SE')} verifikat · {counts.invoices} fakturor ·
					{' '}{counts.active_customers} aktiva kunder · historik från {data.first_month}
				</span>
				<button className="btn" onClick={load} disabled={busy}>
					{busy ? 'Hämtar…' : 'Uppdatera'}
				</button>
			</div>

			{empty ? (
				<div className="state">
					Inga månader i den valda perioden. Data finns från {data.first_month} till {data.cutoff}.
				</div>
			) : (
			<>
			{data.shadow?.active && (
				<div className="panel panel--note">
					<div className="panel__title">Siffrorna innehåller skuggbokföring</div>
					<p>
						{data.shadow.count} preliminära verifikat räknas med
						{data.shadow.months.length
							? ` i ${data.shadow.months.map(m => m.month).join(', ')}` : ''}.
						Detta är inte bokförda utfall. Stäng av under Skuggbokföring för att se
						enbart huvudboken.
					</p>
				</div>
			)}

			<Cards kpi={kpi} />

			<div className="charts--quad">
				<Composition months={months} per={g.sv} />
				<Profitability months={months} per={g.sv} />
				<BalanceLines months={months}
					title={`Kundfordringar och leverantörsskulder per ${g.sv}`}
					hint={`utgående balans vid varje ${g.sv}s slut. Förfallet är den del där förfallodagen passerat — rekonstruerat ur betalningsregistret.`}
					series={[
						{key: 'receivable', label: 'Kundfordringar', emphasis: true, dash: null},
						{key: 'payable', label: 'Leverantörsskulder', dash: '6 4'},
						{key: 'receivable_overdue', label: 'Förfallna kundfordringar', dash: '2 3'},
						{key: 'payable_overdue', label: 'Förfallna leverantörsskulder', dash: '8 3 2 3'},
					]} />
				<BalanceLines months={months}
					title={`Kassa, bank och skattekonto per ${g.sv}`}
					hint={`utgående balans vid varje ${g.sv}s slut, per konto.`}
					series={CASH_SERIES} />
			</div>

			<div className="charts">
				<StackedByKey months={months} field="articles" names={data.article_names}
					title="Fakturerat per artikel"
					hint={`Artikelregistret, per fakturerings${g.sv}. De sju största i perioden; resten som Övriga.`} />
				<StackedByKey months={months} field="accounts" names={data.account_names}
					title="Intäkt per konto"
					hint={`bokförd intäkt per 3xxx-konto och ${g.sv}, ur huvudboken.`} />
			</div>

			{partial && (
				<div className="panel">
					<div className="panel__title">{partialName[0].toUpperCase() + partialName.slice(1)} är delvis bokförd</div>
					<p>
						{partialWhy}. Intäkt, kostnad, resultat och bruttomarginal för {partialName} ska därför
						inte läsas som utfall — stapeln är grå i diagrammet och raden märkt i tabellen. MRR
						kommer däremot ur fakturaregistret, som är fullständigt, så varje kort bär etiketten för
						sin källa. Burn och runway räknas på de tre senaste helt bokförda månaderna.
					</p>
				</div>
			)}

			<MonthTable months={months} />

			<div className="table-wrap">
				<table style={{minWidth: 640}}>
					<thead><tr>
						<th>Intäktsström</th><th>Artikelserie</th><th>Konto</th>
						<th>MRR</th><th>Artiklar</th><th>Fakturerat jan–jul</th>
					</tr></thead>
					<tbody>
						{[...data.streams].sort((a, b) => b.invoiced_2026_h1 - a.invoiced_2026_h1).map(s => (
							<tr key={s._id}>
								<td>{s.name}</td><td>{s.series}xx</td>
								<td>{s.account}{s.account_eu ? ` / ${s.account_eu}` : ''}</td>
								<td>{s.is_mrr ? 'ja' : '—'}</td><td>{s.articles}</td>
								<td>{kr(s.invoiced_2026_h1)}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			</>
			)}

			<p className="foot-note">
				MRR är enbart abonnemang. Förskottsbetalningar sprids över sin fakturaperiod, så en
				halvårsfaktura läses som sex månader och inte som en topp. Moduler, order- och
				transaktionsavgifter, onboarding och utveckling räknas inte in — de är återkommande
				men följer inte licensbasen. NRR och GRR beräknas ur summerade rörelser, aldrig som
				medelvärde av månadskvoter. Huvudbokssiffrorna stämmer krona för krona mot
				budgetarbetsboken.
			</p>
		</Chrome>
	);
}
